import React, { useContext, useEffect, useState } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import Menu, { MENU_CATEGORY } from '../../../organisms/menu/Menu';
import Paragraph from '../../../atoms/paragraphs/Paragraph';
import Span from '../../../atoms/spans/Span';
import HandleCallResultModal from '../../feedback/modal/HandleCallResultModal';
import Button from '../../../atoms/buttons/Button';
import { SettingService } from '../../../../services/SettingService';
import { AppContext } from '../../../../contexts/AppContext';
import { TaxesHelper } from './TaxesHelper';
import { getCountries } from '../../util/api/document-customization/request';
import AddEditTaxModal from '../../../organisms/modals/taxes/AddEditTaxModal';
import DeleteTaxConfirmationModal from '../../../organisms/modals/taxes/DeleteTaxConfirmationModal';
import SimpleTable from '../../../organisms/tables/SimpleTable';

const TaxesLayout = (props) => {
  const { appState } = useContext(AppContext);
  const [isLoading, setIsLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [callSucceeded, setCallSucceeded] = useState(false);
  const [callResponse, setCallResponse] = useState(null);
  const [taxes, setTaxes] = useState([]);
  const [countries, setCountries] = useState([]);
  const taxCodes = TaxesHelper.taxCodes(props.intl);
  const [isEdit, setIsEdit] = useState(false);
  const [taxToSubmit, setTaxToSubmit] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [taxToDelete, setTaxToDelete] = useState(null);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [hasNameError, setHasNameError] = useState(false);

  const onChangeField = (field, value) => {
    if (field === 'default_tax') {
      value = value ? 1 : 0;
    }

    if (field === 'name') {
      let hasNameError = false;
      let taxName = value.toLowerCase();
      taxes.forEach((tax) => {
        if (
          tax.name.toLowerCase() === taxName.trim() &&
          tax.id !== taxToSubmit.id
        ) {
          hasNameError = true;
          return;
        }
      });
      setHasNameError(hasNameError);
    }

    const newTax = { ...taxToSubmit, [field]: value };
    setTaxToSubmit(newTax);
  };

  const addTax = () => {
    setIsEdit(false);
    setTaxToSubmit({
      region: countries[0].region,
      code: taxCodes[0].code,
      name: '',
      value: '',
      default_tax: 0,
    });
    setShowModal(true);
  };

  const editTax = (tax) => {
    setIsEdit(true);
    setTaxToSubmit(tax);
    setShowModal(true);
  };

  const deleteTax = (tax) => {
    if (taxes.length === 1) {
      return;
    }

    setTaxToDelete(tax);
    setShowConfirmationModal(true);
  };

  const submitTax = async () => {
    setSubmitting(true);
    setIsSubmitted(true);

    let taxToUse = structuredClone(taxToSubmit);
    taxToUse.value = taxToUse.value.replace(/,/g, '.');
    taxToUse.value = taxToUse.value.replace(/ %/g, '');
    taxToUse.value = parseFloat(taxToUse.value).toFixed(2);
    taxToUse.default_tax = taxToUse.default_tax ? 1 : 0;
    taxToUse.code = taxToUse.code ? taxToUse.code : taxCodes[0].code;

    const response = isEdit
      ? await SettingService.updateTax(taxToUse.id, taxToUse)
      : await SettingService.addTax(taxToUse);

    if (!response.error) {
      setCallSucceeded(true);
      setCallResponse(
        props.intl.messages[
          `${isEdit ? 'taxesEditSuccess' : 'taxesCreateSuccess'}`
        ]
      );

      const updatedTaxes = structuredClone(taxes);

      if (!isEdit) {
        updatedTaxes.push(response.tax);
      }

      if (response.tax.default_tax === 1) {
        updatedTaxes.forEach((tax) => {
          if (tax.id !== response.tax.id) tax.default_tax = 0;
        });
      }
      const index = updatedTaxes.findIndex((tax) => tax.id === response.tax.id);
      updatedTaxes[index] = response.tax;

      setTaxes(await TaxesHelper.handleTaxValues(updatedTaxes));
    } else {
      setCallSucceeded(false);
      setCallResponse(response.error);
    }

    setShowModal(false);
    setSubmitting(false);
    setIsEdit(false);
    setTaxToSubmit(null);
  };

  const submitDeleteTax = async () => {
    setSubmitting(true);
    setIsSubmitted(true);

    const response = await SettingService.deleteTax(taxToDelete.id);

    if (!response.error) {
      setCallSucceeded(true);
      setCallResponse(props.intl.messages['taxesDeleteSuccess']);
      let updatedTaxes = structuredClone(taxes);
      const index = updatedTaxes.findIndex((t) => t.id === taxToDelete.id);
      updatedTaxes.splice(index, 1);

      const updatedTaxesCopy = updatedTaxes;
      if (taxToDelete.default_tax === 1) {
        updatedTaxesCopy[0].default_tax = 1;

        let taxToUse = structuredClone(updatedTaxesCopy[0]);
        taxToUse.value = taxToUse.value.replace(/,/g, '.');
        taxToUse.value = taxToUse.value.replace(/ %/g, '');
        taxToUse.value = parseFloat(taxToUse.value).toFixed(2);

        const response = await SettingService.updateTax(taxToUse.id, taxToUse);

        const reUpdatedTaxes = structuredClone(updatedTaxesCopy);
        const index = updatedTaxes.findIndex((tax) => tax.id === taxToUse.id);
        updatedTaxes[index] = response.tax;

        setTaxes(await TaxesHelper.handleTaxValues(reUpdatedTaxes));
      } else {
        setTaxes(updatedTaxes);
      }
    } else {
      setCallSucceeded(false);
      setCallResponse(response.error);
    }

    setShowModal(false);
    setSubmitting(false);
    setShowConfirmationModal(false);
    setTaxToDelete(null);
  };

  useEffect(() => {
    const fetchTaxes = async () => {
      const response = await SettingService.getTaxes();

      if (!response.error) {
        setTaxes(await TaxesHelper.handleTaxValues(response.taxes));
        setIsLoading(false);
      }
    };

    const fetchCountries = async () => {
      const response = await getCountries(appState.accountId, () => {});

      if (!response.error) {
        setCountries(
          TaxesHelper.countriesTaxesManipulation(
            response.tax_countries,
            props.intl
          )
        );
      }
    };

    fetchTaxes();
    fetchCountries();

    // eslint-disable-next-line
  }, []);

  const tableConfig = {
    name: {
      headerClassName: '',
      headerValue: props.intl.messages['taxesName'],
      rowClassName: 'font-bold color-gray-700',
    },
    value: {
      headerClassName: '',
      headerValue: props.intl.messages['taxesValue'],
      rowClassName: 'color-gray-600',
    },
    region: {
      headerClassName: 'd-none d-lg-block',
      headerValue: props.intl.messages['taxesRegion'],
      rowClassName: 'd-none d-lg-block font-light color-gray-600',
      formatValue: (taxRegion) => {
        return countries.find((country) => country.region === taxRegion)?.name;
      },
    },
    default_tax: {
      headerClassName: 'text-center',
      headerValue: props.intl.messages['taxesDefault'],
      rowClassName: 'd-flex color-gray-600 justify-content-center',
      formatValue: (taxDefault) => {
        return (
          <div
            data-testid={`default-tax-indicator-${taxDefault === 1 ? 'active' : 'inactive'}`}
            className={`tax-default-circle ${taxDefault === 1 ? 'bg-green-100' : 'bg-gray-200'}`}
            style={{ height: '12px', width: '12px', borderRadius: '50%' }}
          ></div>
        );
      },
    },
    code: {
      headerClassName: 'd-none d-lg-block text-center',
      headerValue: props.intl.messages['taxesCode'],
      rowClassName: 'd-none d-lg-block font-light color-gray-600 text-center',
      formatValue: (taxCode) => {
        return taxCodes.find((code) => code.code === taxCode)?.name;
      },
    },
  };

  const tableActions = {
    rowAction: editTax,
  };

  const footerInfo = (
    <FormattedMessage
      id='taxesDefaultDescription'
      values={{
        b: (...chunks) => (
          <>
            <Span className='bold'>{chunks}</Span>&nbsp;
          </>
        ),
      }}
    />
  );

  return (
    <div id='main-content' className='container --taxes'>
      <div className='row mx-0 justify-content-center'>
        <div className='col-lg-9 ps-md-0'>
          <div className='d-flex flex-column w-100'>
            <div className='col-12 px-0'>
              <Paragraph
                id='billing-preferences-title'
                className='text-header h2'
              >
                {props.intl.messages['taxesTitle']}
              </Paragraph>

              <Paragraph className='text-paragraph mt-3 mb-2'>
                <Span className='d-block'>
                  {props.intl.messages['taxesDescription']}
                </Span>
              </Paragraph>

              <div className='sep-light mt-2' />
            </div>

            <div className='d-flex flex-column w-100 mt-4'>
              <Button
                className='button button-primary me-auto'
                id='add-tax-button'
                iconClassName='icon fas fa-plus pe-3'
                label={props.intl.messages['taxesAddTax']}
                onClick={addTax}
              />

              <SimpleTable
                isLoading={isLoading}
                config={tableConfig}
                data={taxes}
                actions={tableActions}
                footerInfo={footerInfo}
              />
            </div>
          </div>
        </div>
        <div className='col-lg-3 pe-md-0'>
          <Menu {...props} activeCategory={MENU_CATEGORY.PERSONALIZATION} />
        </div>
      </div>

      {(showModal && (
        <AddEditTaxModal
          showModal={showModal}
          changeModalVisibility={() => {
            setShowModal(false);
            setHasNameError(false);
          }}
          isEdit={isEdit}
          numTaxes={taxes.length}
          name={taxToSubmit?.name || ''}
          nameError={hasNameError}
          value={taxToSubmit?.value || ''}
          region={
            countries.find(
              (country) => country.region === taxToSubmit?.region
            ) || ''
          }
          code={taxCodes.find((code) => code.code === taxToSubmit?.code) || ''}
          regions={countries}
          codes={taxCodes}
          isDefault={taxToSubmit?.default_tax === 1 || false}
          onChangeField={onChangeField}
          saveTax={() => {
            submitTax();
          }}
          deleteTax={() => {
            deleteTax(taxToSubmit);
          }}
        />
      )) || <></>}

      {(showConfirmationModal && (
        <DeleteTaxConfirmationModal
          showModal={showConfirmationModal}
          title={'taxesDeleteMessage'}
          confirmButtonId={'taxesDelete'}
          onConfirmationFunction={submitDeleteTax}
          changeModalVisibility={() => {
            setShowConfirmationModal(false);
            setTaxToDelete(null);
          }}
        />
      )) || <></>}

      {(isSubmitted && (
        <HandleCallResultModal
          isLoading={submitting}
          succeeded={callSucceeded}
          message={callResponse}
          onClose={() => {
            setShowModal(false);
            setShowConfirmationModal(false);
            setIsSubmitted(false);
            setCallResponse(null);
          }}
          showCloseButton={true}
        />
      )) || <></>}
    </div>
  );
};

export default injectIntl(TaxesLayout);
